import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPost = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error("Failed to fetch blog post:", error);
      setError("Post not found");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="noise-texture pt-20" data-testid="blog-post-loading">
        <div className="section-padding">
          <div className="container-custom max-w-4xl">
            <Skeleton className="h-8 w-32 mb-8" />
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-6 w-1/2 mb-8" />
            <Skeleton className="aspect-video w-full rounded-2xl mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="noise-texture pt-20" data-testid="blog-post-error">
        <div className="section-padding">
          <div className="container-custom max-w-4xl text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-slate-100 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h1 className="heading-2 text-slate-900 mb-4">Post Not Found</h1>
            <p className="body-base mb-8">The blog post you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button className="btn-primary">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="noise-texture pt-20" data-testid="blog-post-page">
      <article className="section-padding">
        <div className="container-custom max-w-4xl">
          {/* Back Button */}
          <Link to="/blog" className="inline-flex items-center text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-8" data-testid="blog-post-header">
            <span className="badge mb-4" data-testid="blog-post-category">{post.category}</span>
            <h1 className="heading-1 text-slate-900 mb-6" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2" data-testid="blog-post-author">
                <User className="w-5 h-5" />
                {post.author}
              </span>
              <span className="flex items-center gap-2" data-testid="blog-post-date">
                <Calendar className="w-5 h-5" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-12 bg-slate-100" data-testid="blog-post-image">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-slate prose-lg max-w-none" data-testid="blog-post-content">
            {post.content.split('\n\n').map((paragraph, index) => {
              // Check if it's a heading (starts with **)
              if (paragraph.startsWith('**') && paragraph.includes(':**')) {
                const headingMatch = paragraph.match(/\*\*(.+?):\*\*/);
                if (headingMatch) {
                  const heading = headingMatch[1];
                  const rest = paragraph.replace(/\*\*(.+?):\*\*/, '').trim();
                  return (
                    <div key={index}>
                      <h2 className="heading-3 text-slate-900 mt-8 mb-4">{heading}</h2>
                      {rest && <p className="body-base mb-4">{rest}</p>}
                    </div>
                  );
                }
              }
              
              // Check if it's a list item
              if (paragraph.match(/^\d+\.\s\*\*/)) {
                const items = paragraph.split('\n').filter(item => item.trim());
                return (
                  <ul key={index} className="list-none space-y-3 my-6">
                    {items.map((item, itemIndex) => {
                      const match = item.match(/^\d+\.\s\*\*(.+?)\*\*:?\s*(.*)$/);
                      if (match) {
                        return (
                          <li key={itemIndex} className="flex gap-3">
                            <span className="font-semibold text-slate-900">{match[1]}:</span>
                            <span className="text-slate-600">{match[2]}</span>
                          </li>
                        );
                      }
                      return <li key={itemIndex} className="text-slate-600">{item}</li>;
                    })}
                  </ul>
                );
              }

              // Check for bullet points with dashes
              if (paragraph.includes('\n- ')) {
                const parts = paragraph.split('\n- ');
                return (
                  <div key={index}>
                    {parts[0] && <p className="body-base mb-4">{parts[0]}</p>}
                    <ul className="list-disc list-inside space-y-2 my-4">
                      {parts.slice(1).map((item, itemIndex) => (
                        <li key={itemIndex} className="text-slate-600">{item}</li>
                      ))}
                    </ul>
                  </div>
                );
              }

              return (
                <p key={index} className="body-base mb-4">
                  {paragraph}
                </p>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link to="/blog">
              <Button className="btn-secondary" data-testid="blog-post-back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to All Posts
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
